import React, { type ComponentType } from "react";
import type { RouteObject } from "react-router-dom";
import type { Middleware, Reducer } from "@reduxjs/toolkit";
import { faultExchangeApi } from "@/features/faultExchangeSlice/services";
import { blockInputApi } from "@/features/powerRootSlice/services/blockInputApi";
import { createBlockScheduleApi } from "@/features/powerRootSlice/services/createBlockScheduleApi";
import { createPowerScheduleApi } from "@/features/powerRootSlice/services/createScheduleApi";
import { schedulePowerApi } from "@/features/powerRootSlice/services/powerApi";
import {
  bmInstructionExchangeApi,
  energyExchangeApi,
  energyScheduleExchangeApi,
  entityExchangeApi,
  profileCreatorApi,
} from "@/features/scheduleEnergyRootSlice/services";
import { isFeatureSwitchEnabled } from "@/helpers/switch.helpers";
import RewriteRoute from "@/pages/RewriteRoute";
import { injectSlice } from "@/store";
import { dynamicMiddleware } from "@/store";
import {
  dynamicSlicesNames,
  dynamicSlicesNamesKeys,
  type LazyLoadedSlices,
} from "@/store.constants";
import type { UserPermissions } from "@/types/data";
import { getUserPermissions } from "./getUserPermissions";
import {
  buildExtRoute,
  isExcludedEnvRoute,
  navItemsMap,
  userHasPermission,
} from "./router.helpers";
import type { Role } from "./types";

type ReducerPath = keyof LazyLoadedSlices;
type DynamicSlicesName = keyof typeof dynamicSlicesNames;
export type ApiSlice = {
  reducerPath: ReducerPath;
  reducer: ReturnType<Reducer>;
  middleware: Middleware;
};

const injectFeatureSlice = (name: DynamicSlicesName, slice: Reducer) => {
  const reducerPath = dynamicSlicesNames[name];
  injectSlice<typeof reducerPath>({
    reducerPath,
    reducer: slice as ReturnType<Reducer>,
  });
};

const injectApiSlices = (apis: ApiSlice[]) => {
  apis.forEach((api) => {
    injectSlice<typeof api.reducerPath>({
      reducerPath: api.reducerPath,
      reducer: api.reducer,
    });
    // duplication checked by default
    // https://redux-toolkit.js.org/api/configureStore#duplicatemiddlewarecheck
    dynamicMiddleware.addMiddleware(api.middleware);
  });
};

type LazyLoadFn<T = unknown> = () => Promise<T>;

const defaultPermissionLoader = () => Promise.resolve({} as UserPermissions);

/**
 * Returns a RouteObject with its lazy-loaded component.
 * Also injects the redux slices for the feature and required apis.
 *
 * Note: The bundler cannot discover programmatic imports, see
 * https://github.com/rollup/plugins/tree/master/packages/dynamic-import-vars#limitations
 *
 * @param path the route url path
 * @param componentPromise function that dynamically imports the page
 * @param sliceName optional feature slice name constant
 * @param slicePromise optional function that dynamically imports feature slice
 * @param apiSlices optional array api slice dependencies
 * @param loader optional route loader used for permission guards
 */
export const buildRoute = (
  path: string,
  componentPromise: LazyLoadFn<{ Component: ComponentType }>,
  sliceName?: DynamicSlicesName,
  slicePromise?: LazyLoadFn<{ sliceReducer: Reducer }>,
  apiSlices: ApiSlice[] = [],
  loader: LazyLoadFn<UserPermissions> = defaultPermissionLoader
): RouteObject => {
  return {
    path,
    loader,
    async lazy() {
      const { Component } = await componentPromise();
      if (!Component) {
        throw new Error(`Failed to load component for route ${path}`);
      }

      // Allow for page with no corresponding feature slice
      if (slicePromise && sliceName) {
        const { sliceReducer } = await slicePromise();
        if (!sliceReducer) {
          throw new Error(`Failed to load feature slice for route ${path}`);
        }
        injectFeatureSlice(sliceName, sliceReducer);
      }
      if (apiSlices.length) {
        injectApiSlices(apiSlices);
      }

      return { Component };
    },
  };
};

/**
 * Builds an array of app routes for a given user role.
 *
 * When introducing a new page you should add a case to the switch statement.
 */
export const getChildrenForRoutes = (userRole: Role[]) => {
  const routes: RouteObject[] = [
    {
      index: true,
      element: <RewriteRoute path="/home" />,
    },
  ];

  const lazyRoutes = Object.keys(navItemsMap).map((route) => {
    const navItem = navItemsMap[route];
    const path = route;

    // Filter by environment exclusion and permissions for all routes
    if (
      isExcludedEnvRoute(navItem) ||
      !userHasPermission(userRole, navItem.roles)
    ) {
      return null;
    }

    // Check exclude flag only for non-external routes (handled in switch)
    // External routes should remain routable even if excluded from navigation
    const isExternalRoute = navItem.isExternal;
    if (!isExternalRoute && navItem.exclude) {
      return null;
    }

    switch (route) {
      case "/scheduling/energy": {
        return buildRoute(
          path,
          () => import("@/pages/Energy"),
          dynamicSlicesNamesKeys.SCHEDULE_ENERGY,
          () => import("@/features/scheduleEnergyRootSlice"),
          [
            energyExchangeApi,
            entityExchangeApi,
            profileCreatorApi,
            energyScheduleExchangeApi,
          ],
          getUserPermissions("/energy-exchange/user", path)
        );
      }
      case "/markets/bm-instructions": {
        return buildRoute(
          path,
          () => import("@/pages/BMInstructions"),
          dynamicSlicesNamesKeys.BM_INSTRUCTIONS,
          () => import("@/features/bmInstructionsSlice"),
          [bmInstructionExchangeApi],
          getUserPermissions("/bm-instruction-exchange/api/user", path)
        );
      }
      case "/scheduling/power": {
        return buildRoute(
          path,
          () => import("@/pages/Power"),
          dynamicSlicesNamesKeys.POWER,
          () => import("@/features/powerRootSlice"),
          [
            schedulePowerApi,
            createPowerScheduleApi,
            createBlockScheduleApi,
            blockInputApi,
          ],
          getUserPermissions("/schedule-exchange/user", path)
        );
      }
      case "/explorer": {
        const revenueChildren = [
          {
            index: true,
            element: <RewriteRoute path="/explorer/commercial/overview" />,
          },
          buildRoute(
            "overview",
            () => import("@/pages/Explorer/RevenueExplorer"),
            dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
            () => import("@/features/portfolioExplorerSlice")
          ),
          buildRoute(
            "penalties",
            () => import("@/pages/Explorer/RevenueExplorer"),
            dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
            () => import("@/features/portfolioExplorerSlice")
          ),
        ];

        const technicalInsightsChildren = [
          {
            index: true,
            element: (
              <RewriteRoute path="/explorer/technical-insights/overview" />
            ),
          },
          buildRoute(
            "overview",
            () => import("@/pages/Explorer/TechnicalInsightsExplorer"),
            dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
            () => import("@/features/portfolioExplorerSlice")
          ),
        ];

        const benchmarkingChildren = [
          {
            index: true,
            element: <RewriteRoute path="/explorer/benchmarking/competitor" />,
          },
          buildRoute(
            "competitor",
            () => import("@/pages/Explorer/BenchmarkingExplorer"),
            dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
            () => import("@/features/portfolioExplorerSlice")
          ),
        ];

        if (userHasPermission(userRole, ["admin", "asset"])) {
          technicalInsightsChildren.push(
            buildRoute(
              "availability",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer"),
              dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
              () => import("@/features/portfolioExplorerSlice")
            ),
            buildRoute(
              "cycling",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer"),
              dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
              () => import("@/features/portfolioExplorerSlice")
            ),
            buildRoute(
              "efficiency",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer"),
              dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
              () => import("@/features/portfolioExplorerSlice")
            ),
            buildRoute(
              "misdelivery",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer"),
              dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
              () => import("@/features/portfolioExplorerSlice")
            )
          );
        }

        return {
          path,
          children: [
            {
              index: true,
              element: <RewriteRoute path="/explorer/commercial/overview" />,
            },
            {
              path: "commercial",
              children: revenueChildren,
            },
            {
              path: "technical-insights",
              children: technicalInsightsChildren,
            },
            {
              path: "benchmarking",
              children: benchmarkingChildren,
            },

            buildRoute(
              "explore-asset/:assetId",
              () => import("@/pages/Explorer/"),
              dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
              () => import("@/features/portfolioExplorerSlice")
            ),
          ],
        };
      }
      case "/trading/trade-hub": {
        const parentRoute = buildRoute(
          path,
          () => import("@/pages/TradeHub/TradeHubLayout"),
          dynamicSlicesNamesKeys.TRADE_HUB,
          () => import("@/features/tradeHubSlice")
        );

        const children: RouteObject[] = [];

        // Add optimiser hub route if feature toggle is enabled
        if (isFeatureSwitchEnabled("optimiser-hub")) {
          children.push(
            {
              index: true,
              element: <RewriteRoute path="/trading/trade-hub/optimiser" />,
            },
            buildRoute(
              "optimiser",
              () => import("@/pages/TradeHub/OptimiserHub")
            )
          );
        }

        // Future tabs can be added here with their own feature toggles:
        // if (isFeatureSwitchEnabled("new-tab-feature")) {
        //   children.push(
        //     buildRoute(
        //       "new-tab",
        //       () => import("@/pages/TradeHub/NewTab")
        //     )
        //   );
        // }

        return {
          ...parentRoute,
          children,
        };
      }

      case "/administration/fault": {
        return buildRoute(
          path,
          () => import("@/pages/Fault"),
          undefined,
          undefined,
          [faultExchangeApi]
        );
      }
      default:
        return buildExtRoute(route);
    }
  });

  return routes.concat(
    lazyRoutes.filter((route): route is RouteObject => route !== null)
  );
};
